import React from 'react'
import { graphql } from 'gatsby'
import Helmet from 'react-helmet'
import styled from 'styled-components'
import favicon from '../images/favicon.ico'
import Button from '../components/Button'
import ClientList from '../components/ClientList'
import Container from '../Layout/Container'
import CTA from '../sections/CTA'
import CustomerSatisfaction from '../sections/CustomerSatisfaction'
import DeployYourMessage from '../sections/DeployYourMessage'
import DoMoreWithEmail from '../sections/DoMoreWithEmail'
import FeatureList from '../components/FeatureList'
import GeneralSection from '../sections/GeneralSection'
import ImageAndHeader from '../sections/ImageAndHeader'
import Layout from '../Layout/Layout'
import MailjetLogo from '../assets/svg/mailjet-logo.svg'
import MainPageHero from '../sections/MainPageHero'
import SEO from '../components/seo'
import SVG from 'react-inlinesvg'
import TextHeader from '../components/TextHeader'

const StyledLeftColumn = styled.div`
  padding: 32px 0;
  
  ${({ theme }) => theme.media.desktop} {
    padding: 0 32px 64px 0;
  }
`

const StyledRightColumn = styled.div`
  padding: 32px 0;

  ${({ theme }) => theme.media.desktop} {
    padding: 0 0 24px 42px;
  }
`

const IndexPage = ({ data }) => {
  const homeData = data.contentfulHomePage

  return (
    <>
      <Layout overflowHidden="true">
        <SEO title="Mailgun Technologies" type="website" />
        <Helmet defer={false}>
          <title>Transactional Email API Service For Developers</title>
          <meta
            name="description"
            content="Powerful Transactional Email APIs that enable you to send, receive, and track emails, built with developers in mind. Learn more today!"
          />
          <meta name="robots" content="index,follow" />
          <link rel="canonical" href="https://www.mailgun.com/" />
          <link rel="amphtml" href="https://mailgun.com/amp" />
          <link rel="icon" href={favicon} />
        </Helmet>

        <Container fullWidth>
          <MainPageHero
            mainHeader={homeData.heroHeader.heroHeader}
            bottom={<ClientList clients={homeData.heroClientList.items} />}
          >
            <TextHeader
              mdTitle={homeData.heroTextHeader.title.title}
              subtitle={homeData.heroTextHeader.subtitleTop}
              theme={{
                size: 'tiny',
                align: 'left',
              }}
            />
            <Button
              cta="true"
              external={homeData.tryMailgunButton.external}
              isAbsolutePath={homeData.tryMailgunButton.isAbsolutePath}
              primary="true"
              to={homeData.tryMailgunButton.buttonLink}
            >
              {homeData.tryMailgunButton.buttonText}
            </Button>
          </MainPageHero>
        </Container>

        <Container>
          <GeneralSection>
            <TextHeader
              mdTitle={homeData.inGoodCompanyHeader.title.title}
              subtitle={homeData.inGoodCompanyHeader.subtitleTop}
              text={homeData.inGoodCompanyHeader.text.text}
              theme={{
                size: 'big',
                align: 'center',
              }}
            />
            <FeatureList features={homeData.inGoodCompanyFeatures.items} />
          </GeneralSection>
        </Container>

        <Container fullWidth greyBG>
          <GeneralSection>
            <ImageAndHeader
              smallPadding="true"
              leftColumn={{
                width: 50,
                data: (
                  <StyledLeftColumn>
                    <TextHeader
                      mdTitle={homeData.mjHeader[0].title.title}
                      subtitle={homeData.mjHeader[0].subtitleTop}
                      text={homeData.mjHeader[0].text.text}
                      theme={{
                        size: 'tiny',
                        align: 'left',
                      }}
                    />
                    <Button
                      external={homeData.mjHeader[1].external}
                      isAbsolutePath={homeData.mjHeader[1].isAbsolutePath}
                      mailjetdark="true"
                      to={homeData.mjHeader[1].buttonLink}
                    >
                      <SVG src={MailjetLogo} />
                      {
                        homeData.mjHeader.filter(
                          item => item.__typename === 'ContentfulButton'
                        )[0].buttonText
                      }
                    </Button>
                  </StyledLeftColumn>
                ),
              }}
              rightColumn={{
                width: 50,
                data:                 
                  <img
                    alt='mailjet'
                    loading="lazy"
                    src={require('../images/mailjet/mailjetImage.png')}
                    srcSet={`
                      ${require('../images/mailjet/mailjetImage@2x.png')} 2x, 
                    `}
                    width="100%"
                  />,
              }}
            />
          </GeneralSection>
        </Container>

        <Container fullWidth>
          <GeneralSection>
            <ImageAndHeader
              mobileReverse
              shiftX="true"
              rightColumn={{
                width: 40,
                data: (
                  <StyledRightColumn>
                    <TextHeader
                      mdTitle={
                        homeData.deliverHeader.filter(
                          item => item.__typename === 'ContentfulTextHeader'
                        )[0].title.title
                      }
                      subtitle={
                        homeData.deliverHeader.filter(
                          item => item.__typename === 'ContentfulTextHeader'
                        )[0].subtitleTop
                      }
                      text={
                        homeData.deliverHeader.filter(
                          item => item.__typename === 'ContentfulTextHeader'
                        )[0].text.text
                      }
                      theme={{
                        size: 'tiny',
                        align: 'left',
                      }}
                    />
                    <Button
                      ghost="true"
                      external={homeData.deliverHeader.external}
                      to={
                        homeData.deliverHeader.filter(
                          item => item.__typename === 'ContentfulButton'
                        )[0].buttonLink
                      }
                    >
                      {
                        homeData.deliverHeader.filter(
                          item => item.__typename === 'ContentfulButton'
                        )[0].buttonText
                      }
                    </Button>
                  </StyledRightColumn>
                ),
              }}
              leftColumn={{
                width: 60,
                data:   
                  <img
                    alt='deliver'
                    loading="lazy"
                    src={require('../images/deliver/deliverImage.png')}
                    srcSet={`
                      ${require('../images/deliver/deliverImage@2x.png')} 2x, 
                    `}
                  />,
              }}
            />
          </GeneralSection>
        </Container>

        <Container fullWidth greyBG>
          <GeneralSection>
            <ImageAndHeader
              smallPadding="true"
              leftColumn={{
                width: 40,
                data: (
                  <StyledLeftColumn>
                    <TextHeader
                      mdTitle={
                        homeData.verifyHeader.filter(
                          item => item.__typename === 'ContentfulTextHeader'
                        )[0].title.title
                      }
                      subtitle={
                        homeData.verifyHeader.filter(
                          item => item.__typename === 'ContentfulTextHeader'
                        )[0].subtitleTop
                      }
                      text={
                        homeData.verifyHeader.filter(
                          item => item.__typename === 'ContentfulTextHeader'
                        )[0].text.text
                      }
                      theme={{
                        size: 'tiny',
                        align: 'left',
                      }}
                    />
                    <Button
                      ghost="true"
                      external={homeData.verifyHeader.external}
                      to={
                        homeData.verifyHeader.filter(
                          item => item.__typename === 'ContentfulButton'
                        )[0].buttonLink
                      }
                    >
                      {
                        homeData.verifyHeader.filter(
                          item => item.__typename === 'ContentfulButton'
                        )[0].buttonText
                      }
                    </Button>
                  </StyledLeftColumn>
                ),
              }}
              rightColumn={{
                width: 60,
                data: 
                  <img
                    alt='verify'
                    loading="lazy"
                    src={require('../images/verify/verifyImage.png')}
                    srcSet={`
                      ${require('../images/verify/verifyImage@2x.png')} 2x, 
                    `}
                    width="100%"
                  />,
              }}
            />
          </GeneralSection>
        </Container>

        <Container fullWidth>
          <CTA>
            <TextHeader
              mdTitle={
                homeData.deliverabilityServicesTextHeader.filter(
                  item => item.__typename === 'ContentfulTextHeader'
                )[0].title.title
              }
              subtitle={
                homeData.deliverabilityServicesTextHeader.filter(
                  item => item.__typename === 'ContentfulTextHeader'
                )[0].subtitleTop
              }
              text={
                homeData.deliverabilityServicesTextHeader.filter(
                  item => item.__typename === 'ContentfulTextHeader'
                )[0].text.text
              }
              theme={{
                size: 'big',
                align: 'center',
              }}
            />
            <Button
              primary="true"
              cta="true"
              external={homeData.deliverabilityServicesTextHeader.external}
              to={
                homeData.deliverabilityServicesTextHeader.filter(
                  item => item.__typename === 'ContentfulButton'
                )[0].buttonLink
              }
            >
              {
                homeData.deliverabilityServicesTextHeader.filter(
                  item => item.__typename === 'ContentfulButton'
                )[0].buttonText
              }
            </Button>
          </CTA>
        </Container>

        <Container>
          <DeployYourMessage
            cmsData={homeData.deployYourMessageTextHeaderList}
            data={homeData.deployYourMessageCodeSnippetSection}
            header={homeData.deployYourMessageHeader}
          />
        </Container>

        <Container fullWidth greyBG>
          <DoMoreWithEmail
            data={homeData.flagshipProductTextHeaderTimedSlider}
          />
        </Container>

        <Container fullWidth>
          <CustomerSatisfaction>
            <TextHeader
              mdTitle={
                homeData.customerSatisfactionHeaderTestimonials.header.title
                  .title
              }
              subtitle={
                homeData.customerSatisfactionHeaderTestimonials.header
                  .subtitleTop
              }
              text={
                homeData.customerSatisfactionHeaderTestimonials.header.text.text
              }
              theme={{
                size: 'big',
                align: 'center',
              }}
            />
            <Button
              ghost="true"
              external={
                homeData.customerSatisfactionHeaderTestimonials.external
              }
              to={
                homeData.customerSatisfactionHeaderTestimonials.button.buttonLink
              }
            >
              {
                homeData.customerSatisfactionHeaderTestimonials.button.buttonText
              }
            </Button>
          </CustomerSatisfaction>
        </Container>

        <Container fullWidth greyBG>
          <CTA ghost slick>
            <TextHeader
              title={
                <div className="title">
                  It's easy to get started. <span>Let's get sending.</span>
                </div>
              }
              subtitle="sign up"
              text="See what you can accomplish with the world's best email delivery platform."
              theme={{
                size: 'big',
                align: 'center',
              }}
            />
            <Button
              primary="true"
              xxl="true"
              to="https://signup.mailgun.com/"
              external
              isAbsolutePath
            >
              Sign up for Free
            </Button>
          </CTA>
        </Container>
      </Layout>
    </>
  )
}

export const query = graphql`
  query PageQuery {
    contentfulHomePage(name: {eq: "Homepage"}) {
      name
      heroHeader {
        heroHeader
      }
      heroTextHeader {
        id
        subtitleTop
        title {
          title
        }
      }
      heroClientList {
        items {
          id
          iconBw {
            file {
              url
            }
          }
          iconColor {
            file {
              url
            }
          }
        }
      }
      inGoodCompanyHeader {
        subtitleTop
        text {
          text
        }
        title {
          title
        }
      }
      inGoodCompanyFeatures {
        items {
          id
          title
          subtitle
          buttonText
          buttonLink
          externalLink
          isAbsolutePath
          icon {
            file {
              url
            }
          }
        }
      }
      mjHeader {
        ... on ContentfulTextHeader {
          id
          title {
            title
          }
          text {
            text
          }
          subtitleTop
        }
        ... on ContentfulButton {
          id
          name
          buttonText
          buttonLink
          external
          isAbsolutePath
        }
      }
      deliverHeader {
        ... on ContentfulButton {
          id
          name
          buttonText
          buttonLink
          external
        }
        ... on ContentfulTextHeader {
          id
          title {
            title
          }
          text {
            text
          }
          subtitleTop
        }
      }
      verifyHeader {
        ... on ContentfulButton {
          id
          name
          buttonText
          buttonLink
          external
        }
        ... on ContentfulTextHeader {
          id
          title {
            title
          }
          text {
            text
          }
          subtitleTop
        }
      }
      deliverabilityServicesTextHeader {
        ... on ContentfulTextHeader {
          id
          text {
            text
          }
          subtitleTop
          title {
            title
          }
        }
        ... on ContentfulButton {
          id
          buttonText
          buttonLink
          external
        }
      }
      deployYourMessageHeader {
        title {
          title
        }
        text {
          text
        }
        subtitleTop
      }
      deployYourMessageCodeSnippetSection {
        ... on ContentfulCodeSnippetSection {
          id
          name
          codeSnippet {
            id
            items {
              id
              label
              lang
              snippet {
                snippet
              }
            }
          }
          languageStrip {
            items {
              id
              icon {
                file {
                  url
                }
              }
            }
          }
        }
      }
      deployYourMessageTextHeaderList {
        ... on ContentfulTextHeader {
          id
          text {
            text
          }
          title {
            title
          }
          subtitleTop
        }
        ... on ContentfulNumberedList {
          id
          name
          items {
            id
            text {
              text
            }
            name
          }
        }
        ... on ContentfulButton {
          id
          buttonText
          buttonLink
          external
          isAbsolutePath
        }
      }
      flagshipProductTextHeaderTimedSlider {
        header {
          themeSize
          themeAlign
          text {
            text
          }
          title {
            title
          }
          subtitleTop
        }
        items {
          title
          icon {
            fluid {
              ...GatsbyContentfulFluid_noBase64
            }
          }
          id
          subtitle {
            subtitle
          }
        }
      }
      customerSatisfactionHeaderTestimonials {
        button {
          buttonText
          buttonLink
          external
        }
        header {
          subtitleTop
          text {
            text
          }
          title {
            title
          }
        }
      }
      signUpCtaTextHeader {
        ... on ContentfulTextHeader {
          id
          subtitleTop
          text {
            text
          }
          title {
            title
          }
        }
        ... on ContentfulButton {
          id
          name
          buttonText
          buttonLink
          external
          isAbsolutePath
        }
      }
      tryMailgunButton {
        buttonLink
        buttonText
        external
        isAbsolutePath
      }
      demoYoutubeId
    }
  }
`
export default IndexPage
